import React from 'react'
import { PageShell } from './PageShell'
import { StatCard } from '../components/StatCard'
import { useDashboard } from '../DashboardContext'
import { Skeleton } from '../../../components/ui/Skeleton'
import { computeNudgeMetrics } from '../nudgeMetrics'
import type { NudgeQueueSummary } from '../../../types/api'

// ─── Helpers ──────────────────────────────────────────────────────────────────

function safeDiv(n: number, d: number): number {
  return d === 0 ? 0 : n / d
}

function pctStr(n: number, d: number): string {
  if (d === 0) return '—'
  return `${Math.round(safeDiv(n, d) * 100)}%`
}

function daysBetween(a: string, b: string): number {
  return (new Date(b).getTime() - new Date(a).getTime()) / 86_400_000
}

// Report-specific extensions on top of the shared metrics
function computeReportExtras(items: NudgeQueueSummary[]) {
  const base = computeNudgeMetrics(items)

  // Sequence chart: deduplicated by (contactId, sequenceNumber) so a contact
  // who received Nudge #1 in two separate campaigns only counts once per slot.
  const seqCounts: Record<number, { sent: number; replied: number }> = {}
  const seqContactSeen = new Set<string>()
  for (const item of items) {
    if (item.status === 'FAILED') continue
    const key = `${item.contactId}:${item.sequenceNumber}`
    if (seqContactSeen.has(key)) continue
    seqContactSeen.add(key)
    const seq = item.sequenceNumber
    if (!seqCounts[seq]) seqCounts[seq] = { sent: 0, replied: 0 }
    seqCounts[seq].sent++
    if (item.replyReceivedAt != null) seqCounts[seq].replied++
  }

  // RECOVERED-only for avgDaysToRecovery — RETURNED rows have a long gap between
  // recoveredAt and updatedAt (waiting for the owner to log the actual visit),
  // which would skew the metric.
  const recoveredItems = items.filter(i => i.status === 'RECOVERED')
  const avgDaysToRecovery =
    recoveredItems.length === 0
      ? null
      : recoveredItems.reduce((sum, i) => sum + daysBetween(i.createdAt, i.updatedAt), 0) /
        recoveredItems.length

  return { ...base, seqCounts, avgDaysToRecovery }
}

// ─── Visual Funnel ────────────────────────────────────────────────────────────

function FunnelChart({
  totalQueued, totalSent, totalReplied, totalRecovered,
}: {
  totalQueued: number; totalSent: number; totalReplied: number; totalRecovered: number
}) {
  const stages = [
    { label: 'Queued',    count: totalQueued,    color: 'var(--surface3)',  textColor: 'var(--text2)',  prev: null },
    { label: 'Sent',      count: totalSent,      color: 'var(--blue)',      textColor: '#fff',          prev: totalQueued },
    { label: 'Replied',   count: totalReplied,   color: 'var(--teal)',      textColor: '#fff',          prev: totalSent },
    { label: 'Recovered', count: totalRecovered, color: 'var(--green)',     textColor: '#fff',          prev: totalReplied },
  ]
  const max = Math.max(1, totalQueued)

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 0 }}>
      {stages.map((stage, i) => {
        const widthPct = Math.max(12, safeDiv(stage.count, max) * 100)
        const convPct = stage.prev != null ? pctStr(stage.count, stage.prev) : null
        return (
          <React.Fragment key={stage.label}>
            {i > 0 && (
              <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', padding: '4px 0', gap: 0 }}>
                <div style={{ width: 1, height: 6, background: 'var(--border2)' }} />
                {convPct && convPct !== '—' && (
                  <span style={{
                    fontSize: 10, fontFamily: "'DM Mono', monospace",
                    color: 'var(--text3)', background: 'var(--surface2)',
                    borderRadius: 4, padding: '1px 6px', lineHeight: 1.8,
                  }}>{convPct}</span>
                )}
                <div style={{ width: 1, height: 6, background: 'var(--border2)' }} />
              </div>
            )}
            <div style={{ margin: '0 auto', width: `${widthPct}%`, transition: 'width 0.5s ease' }}>
              <div style={{
                background: stage.color,
                borderRadius: 8,
                padding: '11px 14px',
                display: 'flex',
                justifyContent: 'space-between',
                alignItems: 'center',
                opacity: stage.count === 0 && i > 0 ? 0.35 : 1,
              }}>
                <span style={{
                  fontSize: 10, fontFamily: "'DM Mono', monospace",
                  color: stage.textColor, letterSpacing: '0.07em', textTransform: 'uppercase',
                }}>{stage.label}</span>
                <span style={{ fontSize: 22, fontWeight: 600, color: stage.textColor, lineHeight: 1, fontFamily: "'DM Mono', monospace" }}>
                  {stage.count}
                </span>
              </div>
            </div>
          </React.Fragment>
        )
      })}
    </div>
  )
}

// ─── Sequence Bar Chart ───────────────────────────────────────────────────────

function SequenceEffectivenessChart({
  seqCounts,
}: {
  seqCounts: Record<number, { sent: number; replied: number }>
}) {
  const seqs = [1, 2, 3]
  const maxSent = Math.max(1, ...seqs.map(s => seqCounts[s]?.sent ?? 0))
  const chartH = 100
  const hasData = seqs.some(s => (seqCounts[s]?.sent ?? 0) > 0)

  if (!hasData) {
    return (
      <div style={{ fontSize: 13, color: 'var(--text3)', padding: '28px 0', textAlign: 'center' }}>
        No sequence data yet.
      </div>
    )
  }

  return (
    <div>
      <div style={{ display: 'flex', gap: 6, alignItems: 'flex-end', justifyContent: 'space-around', marginBottom: 0 }}>
        {seqs.map(seq => {
          const d = seqCounts[seq] ?? { sent: 0, replied: 0 }
          const sentH = Math.max(d.sent > 0 ? 4 : 0, safeDiv(d.sent, maxSent) * chartH)
          const repliedH = Math.max(d.replied > 0 ? 4 : 0, safeDiv(d.replied, Math.max(1, d.sent)) * sentH)
          return (
            <div key={seq} style={{ flex: 1, display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 6 }}>
              <span style={{ fontSize: 10, fontFamily: "'DM Mono', monospace", color: 'var(--text3)', marginBottom: 4 }}>
                {pctStr(d.replied, d.sent)}
              </span>
              <div style={{ display: 'flex', gap: 4, alignItems: 'flex-end', height: chartH }}>
                <div style={{
                  width: 20, height: sentH,
                  background: 'var(--blue)', borderRadius: '4px 4px 0 0',
                  opacity: d.sent === 0 ? 0.15 : 0.9,
                  transition: 'height 0.5s ease',
                }} />
                <div style={{
                  width: 20, height: repliedH,
                  background: 'var(--teal)', borderRadius: '4px 4px 0 0',
                  opacity: d.replied === 0 ? 0.15 : 1,
                  transition: 'height 0.5s ease',
                }} />
              </div>
              <div style={{ width: '100%', height: 1, background: 'var(--border)' }} />
              <span style={{ fontSize: 11, fontFamily: "'DM Mono', monospace", color: 'var(--text2)' }}>
                Nudge #{seq}
              </span>
              <span style={{ fontSize: 10, color: 'var(--text3)', fontFamily: "'DM Mono', monospace" }}>
                {d.replied}/{d.sent}
              </span>
            </div>
          )
        })}
      </div>
      <div style={{ display: 'flex', gap: 16, marginTop: 16, justifyContent: 'center' }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 5 }}>
          <div style={{ width: 10, height: 10, background: 'var(--blue)', borderRadius: 2, opacity: 0.9 }} />
          <span style={{ fontSize: 11, fontFamily: "'DM Mono', monospace", color: 'var(--text3)' }}>Sent</span>
        </div>
        <div style={{ display: 'flex', alignItems: 'center', gap: 5 }}>
          <div style={{ width: 10, height: 10, background: 'var(--teal)', borderRadius: 2 }} />
          <span style={{ fontSize: 11, fontFamily: "'DM Mono', monospace", color: 'var(--text3)' }}>Replied</span>
        </div>
      </div>
    </div>
  )
}

// ─── Ring Gauge ───────────────────────────────────────────────────────────────

function RingGauge({
  label, numerator, denominator, color, sub,
}: {
  label: string; numerator: number; denominator: number; color: string; sub?: string
}) {
  const pct = denominator === 0 ? 0 : numerator / denominator
  const deg = Math.min(360, Math.round(pct * 360))
  const pctText = denominator === 0 ? '—' : `${Math.round(pct * 100)}%`
  const grad = deg > 0
    ? `conic-gradient(${color} 0deg ${deg}deg, var(--surface3) ${deg}deg 360deg)`
    : 'var(--surface3)'

  return (
    <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 10, padding: '20px 12px' }}>
      <div style={{ width: 80, height: 80, borderRadius: '50%', background: grad, display: 'grid', placeItems: 'center', position: 'relative', flexShrink: 0 }}>
        <div style={{
          width: 56, height: 56, borderRadius: '50%',
          background: 'var(--surface)', position: 'absolute',
          display: 'grid', placeItems: 'center',
        }}>
          <span style={{
            fontSize: 13, fontWeight: 700, lineHeight: 1,
            color: pct > 0 ? color : 'var(--text3)',
            fontFamily: "'DM Mono', monospace",
          }}>{pctText}</span>
        </div>
      </div>
      <div style={{ textAlign: 'center' }}>
        <div style={{ fontSize: 13, fontWeight: 500, color: 'var(--text)', marginBottom: 2 }}>{label}</div>
        <div style={{ fontSize: 11, color: 'var(--text3)', fontFamily: "'DM Mono', monospace" }}>
          {sub ?? `${numerator} of ${denominator}`}
        </div>
      </div>
    </div>
  )
}

// ─── Page ─────────────────────────────────────────────────────────────────────

export function ReportsPage({ isActive }: { isActive: boolean }) {
  const { queueRows, queueLoading } = useDashboard()

  const stats = React.useMemo(() => computeReportExtras(queueRows), [queueRows])
  const loading = queueLoading && queueRows.length === 0

  const totalSentStr      = loading ? '—' : String(stats.totalSent)
  const recoveryRateStr   = loading ? '—' : `${stats.recoveryRatePct}%`
  const optOutRateStr     = loading ? '—' : `${stats.optOutRatePct}%`
  const activeConvsStr    = loading ? '—' : String(stats.activeConvs)
  const avgDaysStr        = loading
    ? '—'
    : stats.avgDaysToRecovery != null
      ? `${stats.avgDaysToRecovery.toFixed(1)}`
      : '—'

  return (
    <PageShell id="page-reports" isActive={isActive}>
      <div className="content">

        {loading && (
          <div aria-busy="true" aria-label="Loading analytics" style={{ display: 'flex', flexDirection: 'column', gap: 14 }}>
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(4,1fr)', gap: 14 }}>
              {[0,1,2,3].map(i => <Skeleton key={i} height={100} borderRadius={14} />)}
            </div>
            <div style={{ display: 'grid', gridTemplateColumns: '2fr 1fr 1fr', gap: 14 }}>
              <Skeleton height={220} borderRadius={14} />
              <Skeleton height={220} borderRadius={14} />
              <Skeleton height={220} borderRadius={14} />
            </div>
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(4,1fr)', gap: 14 }}>
              {[0,1,2,3].map(i => <Skeleton key={i} height={160} borderRadius={14} />)}
            </div>
          </div>
        )}

        {!loading && (
          <>
            {/* ── Stat cards ── */}
            <div className="stats-row">
              <StatCard
                className="sc-blue"
                delay=".05s"
                iconClass="ic-blue"
                icon={
                  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round">
                    <path d="M4 4h16c1.1 0 2 .9 2 2v12c0 1.1-.9 2-2 2H4c-1.1 0-2-.9-2-2V6c0-1.1.9-2 2-2z" />
                    <polyline points="22,6 12,13 2,6" />
                  </svg>
                }
                label="Total Sent"
                value={totalSentStr}
                delta={
                  stats.totalQueued > 0
                    ? <><span className="du">{pctStr(stats.totalSent, stats.totalQueued)}</span><span className="dn"> of queue sent</span></>
                    : <span className="dn">No nudges yet</span>
                }
              />
              <StatCard
                className="sc-green"
                delay=".1s"
                iconClass="ic-green"
                icon={
                  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round">
                    <path d="M3 9l9-7 9 7v11a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2z" />
                    <polyline points="9 22 9 12 15 12 15 22" />
                  </svg>
                }
                label="Recovery Rate"
                value={recoveryRateStr}
                delta={<><span style={{ color: 'var(--green)' }}>{stats.totalRecovered}</span><span className="dn"> customers recovered</span></>}
              />
              <StatCard
                className="sc-orange"
                delay=".15s"
                iconClass="ic-orange"
                icon={
                  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round">
                    <circle cx="12" cy="12" r="10" /><line x1="4.93" y1="4.93" x2="19.07" y2="19.07" />
                  </svg>
                }
                label="Opt-out Rate"
                value={optOutRateStr}
                delta={<><span style={{ color: stats.totalOptOut > 0 ? 'var(--red)' : 'var(--green)' }}>{stats.totalOptOut}</span><span className="dn"> opted out</span></>}
              />
              <StatCard
                className="sc-teal"
                delay=".2s"
                iconClass="ic-teal"
                icon={
                  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round">
                    <path d="M21 15a2 2 0 0 1-2 2H7l-4 4V5a2 2 0 0 1 2-2h14a2 2 0 0 1 2 2z" />
                  </svg>
                }
                label="Active Conversations"
                value={activeConvsStr}
                delta={<><span style={{ color: 'var(--teal)' }}>{stats.totalReplied}</span><span className="dn"> total replies</span></>}
              />
            </div>

            {/* ── Charts row: Funnel + Sequence + Recovery ── */}
            <div style={{ display: 'grid', gridTemplateColumns: '2fr 1fr 1fr', gap: 14, marginBottom: 14 }}>

              {/* Funnel */}
              <div className="panel">
                <div className="panel-header">
                  <div>
                    <div className="panel-title">Nudge Pipeline</div>
                    <div className="panel-sub">Conversion at each stage</div>
                  </div>
                </div>
                <div style={{ padding: '16px 20px 20px' }}>
                  <FunnelChart
                    totalQueued={stats.funnelQueued}
                    totalSent={stats.funnelSent}
                    totalReplied={stats.funnelReplied}
                    totalRecovered={stats.funnelRecovered}
                  />
                </div>
              </div>

              {/* Sequence Effectiveness */}
              <div className="panel">
                <div className="panel-header">
                  <div>
                    <div className="panel-title">Sequence</div>
                    <div className="panel-sub">Replies per nudge #</div>
                  </div>
                </div>
                <div style={{ padding: '12px 20px 20px' }}>
                  <SequenceEffectivenessChart seqCounts={stats.seqCounts} />
                </div>
              </div>

              {/* Recovery Speed */}
              <div className="panel">
                <div className="panel-header">
                  <div>
                    <div className="panel-title">Recovery Speed</div>
                    <div className="panel-sub">Avg days to return</div>
                  </div>
                </div>
                <div style={{ padding: '20px', display: 'flex', flexDirection: 'column', gap: 16 }}>
                  <div style={{ display: 'flex', alignItems: 'flex-end', gap: 6 }}>
                    <span style={{
                      fontSize: 56, fontWeight: 200, lineHeight: 1,
                      color: stats.avgDaysToRecovery != null ? 'var(--text)' : 'var(--text3)',
                      fontFamily: "'DM Mono', monospace",
                    }}>
                      {avgDaysStr}
                    </span>
                    {stats.avgDaysToRecovery != null && (
                      <span style={{ fontSize: 12, color: 'var(--text3)', paddingBottom: 8, fontFamily: "'DM Mono', monospace" }}>
                        days
                      </span>
                    )}
                  </div>

                  <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 8 }}>
                    <div style={{ background: 'var(--surface2)', borderRadius: 8, padding: '10px 12px' }}>
                      <div style={{ fontSize: 9, fontFamily: "'DM Mono', monospace", color: 'var(--text3)', letterSpacing: '0.08em', textTransform: 'uppercase', marginBottom: 4 }}>
                        Recovered
                      </div>
                      <div style={{ fontSize: 24, fontWeight: 300, color: 'var(--green)' }}>
                        {stats.totalRecoveredAll}
                      </div>
                    </div>
                    <div style={{ background: 'var(--surface2)', borderRadius: 8, padding: '10px 12px' }}>
                      <div style={{ fontSize: 9, fontFamily: "'DM Mono', monospace", color: 'var(--text3)', letterSpacing: '0.08em', textTransform: 'uppercase', marginBottom: 4 }}>
                        Visited
                      </div>
                      <div style={{ fontSize: 24, fontWeight: 300, color: 'var(--teal)' }}>
                        {stats.totalReturnedOnly}
                      </div>
                    </div>
                    <div style={{ background: 'var(--surface2)', borderRadius: 8, padding: '10px 12px' }}>
                      <div style={{ fontSize: 9, fontFamily: "'DM Mono', monospace", color: 'var(--text3)', letterSpacing: '0.08em', textTransform: 'uppercase', marginBottom: 4 }}>
                        No Show
                      </div>
                      <div style={{ fontSize: 24, fontWeight: 300, color: 'var(--orange)' }}>
                        {stats.totalNoShow}
                      </div>
                    </div>
                    <div style={{ background: 'var(--surface2)', borderRadius: 8, padding: '10px 12px' }}>
                      <div style={{ fontSize: 9, fontFamily: "'DM Mono', monospace", color: 'var(--text3)', letterSpacing: '0.08em', textTransform: 'uppercase', marginBottom: 4 }}>
                        Exhausted
                      </div>
                      <div style={{ fontSize: 24, fontWeight: 300, color: 'var(--text2)' }}>
                        {stats.totalFailed + stats.totalExhausted}
                      </div>
                    </div>
                  </div>

                  {stats.avgDaysToRecovery == null && (
                    <div style={{ fontSize: 12, color: 'var(--text3)', lineHeight: 1.5 }}>
                      Avg speed appears once customers return.
                    </div>
                  )}
                </div>
              </div>
            </div>

            {/* ── Outcome Rates — ring gauges ── */}
            <div className="panel" style={{ marginBottom: 0 }}>
              <div className="panel-header">
                <div>
                  <div className="panel-title">Outcome Rates</div>
                  <div className="panel-sub">Of all sent nudges</div>
                </div>
              </div>
              <div style={{ display: 'grid', gridTemplateColumns: 'repeat(4, 1fr)', borderTop: '1px solid var(--border)' }}>
                {[
                  { label: 'Recovery Rate',    numerator: stats.funnelRecovered, denominator: stats.funnelSent, color: 'var(--green)',  sub: `${stats.funnelRecovered} recovered` },
                  { label: 'Reply Rate',        numerator: stats.funnelReplied,  denominator: stats.funnelSent,  color: 'var(--teal)',   sub: `${stats.funnelReplied} replied` },
                  { label: 'Opt-out Rate',      numerator: stats.totalOptOut,    denominator: stats.funnelSent,  color: 'var(--red)',    sub: `${stats.totalOptOut} opted out` },
                  { label: 'Exhausted (no reply)', numerator: stats.totalExhaustedNoReply, denominator: stats.funnelSent, color: 'var(--text3)', sub: `${stats.totalExhaustedNoReply} of ${stats.totalExhausted} exhausted` },
                ].map((g, i) => (
                  <div
                    key={g.label}
                    style={{
                      borderRight: i < 3 ? '1px solid var(--border)' : undefined,
                    }}
                  >
                    <RingGauge
                      label={g.label}
                      numerator={g.numerator}
                      denominator={g.denominator}
                      color={g.color}
                      sub={g.sub}
                    />
                  </div>
                ))}
              </div>
            </div>
          </>
        )}

      </div>
    </PageShell>
  )
}
